import { useEffect, useMemo, useRef, useState } from 'react'
import { Tooltip } from 'antd'
import SystemHeader from '@/widgets/layout/SystemHeader'
import { useAppNavigate } from '../../hooks/useAppNavigate'
import { useBackToParent, usePermission, usePersistentPageSize } from '@/hooks'
import { municipalitiesApi, usersApi } from '@/services'
import { useToast } from '@/widgets/common'
import { formatDateTime } from '@/utils'
import useSearchPersistence from '@/hooks/useSearchPersistence'
import { useColumnVisibility } from '@/hooks/useColumnVisibility'
import { Pagination, BackButton, SearchCard, ResizeHandle, SortableHeader, TableLoadingRow, DemoInput, DemoSelect, DemoBadge } from '@/widgets/demo/ListPrimitives';
import { ColumnVisibilityControl } from '@/widgets/demo/ColumnVisibilityControl';
import { demoButtonClass, computePagination, useResizableColumns, useSortState, type ColumnWidthMap, type SortOrder } from '@/widgets/demo/listHelpers';
const USER_MGMT_COLUMN_DEFAULTS: ColumnWidthMap = {
  select: 48,
  userDisplayId: 96,
  municipality: 160,
  loginId: 136,
  name: 168,
  email: 184,
  department: 112,
  role: 88,
  usageStartDate: 112,
  usageEndDate: 112,
  status: 96,
  accountLocked: 136,
  lastLoginAt: 128,
}

const USER_MGMT_COLUMN_ORDER_BASE = [
  'select',
  'userDisplayId',
  'loginId',
  'name',
  'email',
  'department',
  'role',
  'usageStartDate',
  'usageEndDate',
  'status',
  'accountLocked',
  'lastLoginAt',
] as const

const USER_MGMT_COLUMN_ORDER_SYSADMIN = [
  'select',
  'userDisplayId',
  'municipality',
  'loginId',
  'name',
  'email',
  'department',
  'role',
  'usageStartDate',
  'usageEndDate',
  'status',
  'accountLocked',
  'lastLoginAt',
] as const

// 列表示設定（No.30 横展開）用の画面キーと、列ID→日本語ラベルのマップ。
// ラベルは一覧ヘッダの表示テキストと揃える。
const USER_MGMT_COLUMN_VISIBILITY_KEY = 'user-management'

// 選択チェックボックス列(select)と主キー相当のID列(userDisplayId)は常に表示にして、
// 行の選択・同定ができなくなる事故を防ぐ。それ以外は切替可。
const USER_MGMT_ALWAYS_VISIBLE_COLUMNS = ['select', 'userDisplayId']

const USER_MGMT_COLUMN_LABELS: Record<string, string> = {
  userDisplayId: 'ID',
  municipality: '自治体名',
  loginId: 'ログインID',
  name: 'ログインユーザ名',
  email: 'メールアドレス',
  department: '所属',
  role: '権限',
  usageStartDate: '利用開始日',
  usageEndDate: '利用終了日',
  status: 'ステータス',
  accountLocked: 'アカウントロック',
  lastLoginAt: '最終ログイン',
}

interface UserRow {
  id: string
  userDisplayId: string
  loginId: string
  name: string
  email: string
  dept: string
  role: string
  usageStartDate: string
  usageEndDate: string
  status: string
  lastLogin: string
  accountLocked: boolean
  municipalityId: number | null
}

import { USER_ROLE_KEYS, enumLabel } from '@/constants/enums'

const ALL_ROLE_OPTIONS = [
  { value: 'ALL', label: '権限（全て）' },
  ...USER_ROLE_KEYS.map((key) => ({ value: key, label: enumLabel('roles', key) })),
]

const STATUS_OPTIONS = [
  { value: 'ALL', label: 'ステータス（全て）' },
  { value: 'ACTIVE', label: '有効' },
  { value: 'INACTIVE', label: '無効' },
]

// 検索条件は最後に適用した結果を保持する（顧客要望）。適用済みフィルタに加えて
// 並び順とページ番号も localStorage（search_<sub>_user-management）へ保存し、再訪問時に復元する。
interface UserSearchState {
  userDisplayId: string
  loginUserId: string
  name: string
  email: string
  department: string
  role: string
  status: string
  municipalityId: string
  sortBy: string | null
  sortOrder: SortOrder
  page: number
}

const DEFAULT_USER_SEARCH: UserSearchState = {
  userDisplayId: '',
  loginUserId: '',
  name: '',
  email: '',
  department: '',
  role: 'ALL',
  status: 'ALL',
  municipalityId: 'ALL',
  sortBy: null,
  sortOrder: null,
  page: 1,
}

const UserManagement = () => {
  const navigate = useAppNavigate()
  const goBackToParent = useBackToParent()
  const toast = useToast()
  const { isSystemAdmin } = usePermission()
  const [savedSearch, setSavedSearch] = useSearchPersistence<UserSearchState>('user-management', DEFAULT_USER_SEARCH)
  const [page, setPage] = useState(savedSearch.page ?? 1)
  const [pageSize, setPageSize] = usePersistentPageSize('user-management')
  const [rows, setRows] = useState<UserRow[]>([])
  const [total, setTotal] = useState(0)
  const [loading, setLoading] = useState(true)
  const [selectedIds, setSelectedIds] = useState<Set<string>>(() => new Set())
  const selectAllRef = useRef<HTMLInputElement>(null)
  const { sort, setSort, sortQuery } = useSortState({ sortBy: savedSearch.sortBy, order: savedSearch.sortOrder })
  const { widths: colWidths, startResize } = useResizableColumns({ defaults: USER_MGMT_COLUMN_DEFAULTS })
  // システム管理者だけ「自治体名」列を表示する（ハードコード分岐）。
  // ロールで実際に描画する列順（BASE/SYSADMIN）を解決してから列表示設定フックへ渡す。
  const columnOrder = useMemo<readonly string[]>(
    () => (isSystemAdmin ? USER_MGMT_COLUMN_ORDER_SYSADMIN : USER_MGMT_COLUMN_ORDER_BASE),
    [isSystemAdmin],
  )
  const roleOptions = useMemo(
    () => (isSystemAdmin ? ALL_ROLE_OPTIONS : ALL_ROLE_OPTIONS.filter((option) => option.value !== 'SYSTEM_ADMIN')),
    [isSystemAdmin],
  )
  // 列表示設定（No.30 横展開）。ロール解決後の columnOrder を allColumnIds に渡すので、
  // 設定UIのチェック項目もそのロールに見えている列だけになる。hidden 集合方式のため、
  // 別ロールで列数が変わっても未知列は既定表示で安全（壊れない）。
  const { isVisible: isColVisible, setVisible: setColVisible, reset: resetCols, hiddenCount: hiddenColCount } =
    useColumnVisibility(USER_MGMT_COLUMN_VISIBILITY_KEY, columnOrder, {
      alwaysVisible: USER_MGMT_ALWAYS_VISIBLE_COLUMNS,
    })
  const visibleColumnOrder = columnOrder.filter((id) => isColVisible(id))
  // 列表示設定の選択項目（select 列はチェックUIに出さない＝常時表示でラベル不要のため除外）。
  const columnVisibilityItems = useMemo(
    () =>
      columnOrder
        .filter((id) => id !== 'select')
        .map((id) => ({ id, label: USER_MGMT_COLUMN_LABELS[id] ?? id })),
    [columnOrder],
  )
  // 隠した列は colWidths から除外して合計幅を出す（隙間/幅ズレ防止）。
  const tableWidth = visibleColumnOrder.reduce((sum, id) => sum + (colWidths[id] ?? 0), 0)
  // tbody の空行/ローディング行 colSpan は「表示中の列数 + 末尾の余白 col(1)」。
  const visibleColSpan = visibleColumnOrder.length + 1
  const [municipalityNameById, setMunicipalityNameById] = useState<Map<number, string>>(() => new Map())
  const handleSortChange = (next: typeof sort) => {
    setSort(next)
    setPage(1)
  }

  const [qUserDisplayId, setQUserDisplayId] = useState(savedSearch.userDisplayId)
  const [qLoginUserId, setQLoginUserId] = useState(savedSearch.loginUserId)
  const [qName, setQName] = useState(savedSearch.name)
  const [qEmail, setQEmail] = useState(savedSearch.email)
  const [qDept, setQDept] = useState(savedSearch.department)
  const [qRole, setQRole] = useState(savedSearch.role)
  const [qStatus, setQStatus] = useState(savedSearch.status)
  const [qMunicipalityId, setQMunicipalityId] = useState<string>(savedSearch.municipalityId)

  const [applied, setApplied] = useState({
    userDisplayId: savedSearch.userDisplayId,
    loginUserId: savedSearch.loginUserId,
    name: savedSearch.name,
    email: savedSearch.email,
    department: savedSearch.department,
    role: savedSearch.role,
    status: savedSearch.status,
    municipalityId: savedSearch.municipalityId,
  })

  const { totalPages, safePage, rangeFrom, rangeTo } = computePagination(page, total, pageSize)
  const pageRows = rows

  // システム管理者は他自治体ユーザも一覧表示するため、id→自治体名 を事前に取得する。
  // 通常ロールは「自治体名」列を出さないので fetch しない（権限的にも他自治体は見せない方針）。
  useEffect(() => {
    if (!isSystemAdmin) return
    let cancelled = false
    void (async () => {
      try {
        const { data } = await municipalitiesApi.list()
        if (cancelled) return
        setMunicipalityNameById(new Map(data.map((m) => [m.id, m.name])))
      } catch {
        if (!cancelled) setMunicipalityNameById(new Map())
      }
    })()
    return () => {
      cancelled = true
    }
  }, [isSystemAdmin])

  useEffect(() => {
    let cancelled = false
    void (async () => {
      setLoading(true)
      try {
        const { data } = await usersApi.list({
          userDisplayId: applied.userDisplayId.trim() || undefined,
          loginUserId: applied.loginUserId.trim() || undefined,
          name: applied.name.trim() || undefined,
          email: applied.email.trim() || undefined,
          department: applied.department.trim() || undefined,
          role: applied.role === 'ALL' ? undefined : applied.role,
          status: applied.status === 'ALL' ? undefined : applied.status,
          municipalityId: applied.municipalityId === 'ALL' ? undefined : Number(applied.municipalityId),
          ...sortQuery,
          page: safePage - 1,
          size: pageSize,
        })
        if (cancelled) return
        setTotal(data.totalElements)
        setRows(
          data.content.map((u) => ({
            id: String(u.id),
            userDisplayId: u.userDisplayId,
            loginId: u.loginId,
            name: u.name,
            email: u.email,
            dept: u.department || '',
            role: u.role,
            usageStartDate: u.usageStartDate || '－',
            usageEndDate: u.usageEndDate || '－',
            status: u.status,
            lastLogin: u.lastLoginAt ? formatDateTime(u.lastLoginAt) : '－',
            accountLocked: u.accountLocked,
            municipalityId: u.municipalityId ?? null,
          })),
        )
      } catch {
        if (!cancelled) {
          setRows([])
          setTotal(0)
        }
      } finally {
        if (!cancelled) setLoading(false)
      }
    })()
    return () => {
      cancelled = true
    }
    // municipalityNameById は依存に入れない。自治体名はレンダー時に解決するため、
    // 自治体名マップの遅延ロードで一覧を再フェッチしない（初回の二重取得を防止）。
  }, [safePage, pageSize, applied, sortQuery])

  // 適用済みフィルタ・並び順・ページ番号を保存し、再訪問時に前回の検索結果を復元する。
  useEffect(() => {
    setSavedSearch({ ...applied, sortBy: sort.sortBy, sortOrder: sort.order, page })
  }, [applied, sort.sortBy, sort.order, page, setSavedSearch])

  const allPageSelected = pageRows.length > 0 && pageRows.every((r) => selectedIds.has(r.id))
  const somePageSelected = pageRows.some((r) => selectedIds.has(r.id))
  useEffect(() => {
    if (selectAllRef.current) selectAllRef.current.indeterminate = somePageSelected && !allPageSelected
  }, [somePageSelected, allPageSelected])

  const toggleSelectAll = () => {
    const ids = pageRows.map((r) => r.id)
    setSelectedIds((prev) => {
      const next = new Set(prev)
      if (allPageSelected) ids.forEach((id) => next.delete(id))
      else ids.forEach((id) => next.add(id))
      return next
    })
  }

  const toggleRow = (id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  const runSearch = () => {
    setLoading(true)
    setPage(1)
    setApplied({
      userDisplayId: qUserDisplayId,
      loginUserId: qLoginUserId,
      name: qName,
      email: qEmail,
      department: qDept,
      role: qRole,
      status: qStatus,
      municipalityId: qMunicipalityId,
    })
  }

  const runClear = () => {
    setLoading(true)
    setQUserDisplayId('')
    setQLoginUserId('')
    setQName('')
    setQEmail('')
    setQDept('')
    setQRole('ALL')
    setQStatus('ALL')
    setQMunicipalityId('ALL')
    setPage(1)
    setApplied({
      userDisplayId: '',
      loginUserId: '',
      name: '',
      email: '',
      department: '',
      role: 'ALL',
      status: 'ALL',
      municipalityId: 'ALL',
    })
  }

  const openUserEdit = () => {
    if (selectedIds.size === 0) {
      toast.warning('ユーザ情報を編集するには、一覧で対象の1件にチェックを入れてください。')
      return
    }
    if (selectedIds.size >= 2) {
      toast.warning('ユーザ情報を編集するには、1件のみチェックを入れてください。')
      return
    }
    navigate('user-edit', { userId: [...selectedIds][0] })
  }

  return (
    <div className="min-h-screen bg-gray-100">
      <SystemHeader breadcrumbs={[{ label: 'システム管理', page: 'submenu-admin' }, { label: 'ユーザ管理' }]} />
      <div className="mx-auto w-full max-w-[1600px] px-6 py-8">
        <BackButton onClick={goBackToParent} />

        <SearchCard className="mb-6">
          <div className={`mb-4 grid grid-cols-2 gap-4 md:grid-cols-4 ${isSystemAdmin ? 'xl:grid-cols-4' : 'xl:grid-cols-7'}`}>
            <DemoInput type="text" value={qUserDisplayId} onChange={(e) => setQUserDisplayId(e.target.value)} placeholder="ID" />
            <DemoInput type="text" value={qLoginUserId} onChange={(e) => setQLoginUserId(e.target.value)} placeholder="ログインID" />
            <DemoInput type="text" value={qName} onChange={(e) => setQName(e.target.value)} placeholder="ログインユーザ名" />
            <DemoInput type="text" value={qEmail} onChange={(e) => setQEmail(e.target.value)} placeholder="メールアドレス" />
            <DemoInput type="text" value={qDept} onChange={(e) => setQDept(e.target.value)} placeholder="所属" />
            <DemoSelect value={qRole} onChange={(e) => setQRole(e.target.value)}>
              {roleOptions.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </DemoSelect>
            <DemoSelect value={qStatus} onChange={(e) => setQStatus(e.target.value)}>
              {STATUS_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </DemoSelect>
            {isSystemAdmin ? (
              <DemoSelect value={qMunicipalityId} onChange={(e) => setQMunicipalityId(e.target.value)}>
                <option value="ALL">自治体（全て）</option>
                {[...municipalityNameById.entries()].map(([id, name]) => (
                  <option key={id} value={String(id)}>
                    {name}
                  </option>
                ))}
              </DemoSelect>
            ) : null}
          </div>
          <div className="flex justify-end gap-3">
            <button type="button" onClick={runClear} className={demoButtonClass.secondary}>
              クリア
            </button>
            <button type="button" onClick={runSearch} className={demoButtonClass.primary}>
              検索
            </button>
          </div>
        </SearchCard>

        <div className="mb-4 flex flex-wrap items-center gap-3">
          <button type="button" onClick={() => navigate('user-create')} className={demoButtonClass.primarySm}>
            新規ユーザ作成
          </button>
          <button type="button" onClick={openUserEdit} className={demoButtonClass.outline}>
            ユーザ情報編集
          </button>
          <ColumnVisibilityControl
            columns={columnVisibilityItems}
            isVisible={isColVisible}
            setVisible={setColVisible}
            onReset={resetCols}
            alwaysVisible={USER_MGMT_ALWAYS_VISIBLE_COLUMNS}
            hiddenCount={hiddenColCount}
            buttonClassName="ml-auto"
          />
        </div>

        <div className="rounded-xl bg-white p-6 shadow-lg">
          <div className="overflow-x-auto">
            <table className="min-w-full table-fixed" style={{ width: `${tableWidth}px` }}>
              <colgroup>
                {visibleColumnOrder.map((id) => (
                  <col key={id} style={{ width: `${colWidths[id]}px` }} />
                ))}
                <col />
              </colgroup>
              <thead className="border-b-2 border-gray-200 bg-gray-50">
                <tr>
                  {isColVisible('select') && (
                    <th className="px-2 py-3 text-center">
                      <input
                        ref={selectAllRef}
                        type="checkbox"
                        checked={allPageSelected}
                        onChange={toggleSelectAll}
                        className="h-4 w-4 cursor-pointer rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                      />
                    </th>
                  )}
                  {isColVisible('userDisplayId') && (
                    <SortableHeader field="userDisplayId" label="ID" sort={sort} onSortChange={handleSortChange} className="px-2 py-3 text-sm font-medium text-gray-700 normal-case tracking-normal" trailing={<ResizeHandle columnId="userDisplayId" onMouseDown={startResize} />} />
                  )}
                  {isColVisible('municipality') && (
                    <th className="relative px-2 py-3 text-left text-sm font-medium text-gray-700 whitespace-nowrap">
                      自治体名
                      <ResizeHandle columnId="municipality" onMouseDown={startResize} />
                    </th>
                  )}
                  {isColVisible('loginId') && (
                    <SortableHeader field="loginId" label="ログインID" sort={sort} onSortChange={handleSortChange} className="px-2 py-3 text-sm font-medium text-gray-700 normal-case tracking-normal" trailing={<ResizeHandle columnId="loginId" onMouseDown={startResize} />} />
                  )}
                  {isColVisible('name') && (
                    <SortableHeader field="name" label="ログインユーザ名" sort={sort} onSortChange={handleSortChange} className="px-2 py-3 text-sm font-medium text-gray-700 normal-case tracking-normal" trailing={<ResizeHandle columnId="name" onMouseDown={startResize} />} />
                  )}
                  {isColVisible('email') && (
                    <SortableHeader field="email" label="メールアドレス" sort={sort} onSortChange={handleSortChange} className="px-2 py-3 text-sm font-medium text-gray-700 normal-case tracking-normal" trailing={<ResizeHandle columnId="email" onMouseDown={startResize} />} />
                  )}
                  {isColVisible('department') && (
                    <SortableHeader field="department" label="所属" sort={sort} onSortChange={handleSortChange} className="px-2 py-3 text-sm font-medium text-gray-700 normal-case tracking-normal" trailing={<ResizeHandle columnId="department" onMouseDown={startResize} />} />
                  )}
                  {isColVisible('role') && (
                    <th className="relative px-2 py-3 text-left text-sm font-medium text-gray-700">
                      権限
                      <ResizeHandle columnId="role" onMouseDown={startResize} />
                    </th>
                  )}
                  {isColVisible('usageStartDate') && (
                    <SortableHeader field="usageStartDate" label="利用開始日" sort={sort} onSortChange={handleSortChange} className="px-2 py-3 text-sm font-medium text-gray-700 normal-case tracking-normal" trailing={<ResizeHandle columnId="usageStartDate" onMouseDown={startResize} />} />
                  )}
                  {isColVisible('usageEndDate') && (
                    <SortableHeader field="usageEndDate" label="利用終了日" sort={sort} onSortChange={handleSortChange} className="px-2 py-3 text-sm font-medium text-gray-700 normal-case tracking-normal" trailing={<ResizeHandle columnId="usageEndDate" onMouseDown={startResize} />} />
                  )}
                  {isColVisible('status') && (
                    <th className="relative px-2 py-3 text-left text-sm font-medium text-gray-700">
                      ステータス
                      <ResizeHandle columnId="status" onMouseDown={startResize} />
                    </th>
                  )}
                  {isColVisible('accountLocked') && (
                    <th className="relative px-2 py-3 text-left text-sm font-medium text-gray-700 whitespace-nowrap">
                      アカウントロック
                      <ResizeHandle columnId="accountLocked" onMouseDown={startResize} />
                    </th>
                  )}
                  {isColVisible('lastLoginAt') && (
                    <SortableHeader field="lastLoginAt" label="最終ログイン" sort={sort} onSortChange={handleSortChange} className="px-2 py-3 text-sm font-medium text-gray-700 normal-case tracking-normal" />
                  )}
                  <th aria-hidden="true" />
                </tr>
              </thead>
              <tbody className={`divide-y divide-gray-200 transition-opacity ${loading ? 'opacity-60' : ''}`}>
                {pageRows.length === 0 ? (
                  loading ? (
                    <TableLoadingRow colSpan={visibleColSpan} />
                  ) : (
                    <tr>
                      <td colSpan={visibleColSpan} className="px-4 py-8 text-center text-sm text-gray-500">
                        該当するデータがありません
                      </td>
                    </tr>
                  )
                ) : (
                  pageRows.map((r) => (
                      <tr key={r.id} className="transition hover:bg-blue-50">
                        {isColVisible('select') && (
                          <td className="px-2 py-3 text-center">
                            <input
                              type="checkbox"
                              checked={selectedIds.has(r.id)}
                              onChange={() => toggleRow(r.id)}
                              className="h-4 w-4 cursor-pointer rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                            />
                          </td>
                        )}
                        {isColVisible('userDisplayId') && (
                          <td className="px-2 py-3 text-sm font-mono">
                            <Tooltip title={r.userDisplayId} placement="topLeft">
                              <span className="block truncate">{r.userDisplayId}</span>
                            </Tooltip>
                          </td>
                        )}
                        {isColVisible('municipality') && (
                          <td className="px-2 py-3 text-sm">
                            <Tooltip
                              title={(r.municipalityId != null && municipalityNameById.get(r.municipalityId)) || ''}
                              placement="topLeft"
                            >
                              <span className="block truncate">
                                {(r.municipalityId != null && municipalityNameById.get(r.municipalityId)) || '－'}
                              </span>
                            </Tooltip>
                          </td>
                        )}
                        {isColVisible('loginId') && (
                          <td className="px-2 py-3 text-sm font-mono">
                            <Tooltip title={r.loginId} placement="topLeft">
                              <span className="block truncate">{r.loginId}</span>
                            </Tooltip>
                          </td>
                        )}
                        {isColVisible('name') && (
                          <td className="px-2 py-3 text-sm">
                            <Tooltip title={r.name} placement="topLeft">
                              <span className="block truncate">{r.name}</span>
                            </Tooltip>
                          </td>
                        )}
                        {isColVisible('email') && (
                          <td className="px-2 py-3 text-sm">
                            <Tooltip title={r.email} placement="topLeft">
                              <span className="block truncate">{r.email}</span>
                            </Tooltip>
                          </td>
                        )}
                        {isColVisible('department') && (
                          <td className="px-2 py-3 text-sm">
                            <Tooltip title={r.dept} placement="topLeft">
                              <span className="block truncate">{r.dept}</span>
                            </Tooltip>
                          </td>
                        )}
                        {isColVisible('role') && (
                          <td className="px-2 py-3 truncate">
                            <DemoBadge status={r.role} />
                          </td>
                        )}
                        {isColVisible('usageStartDate') && (
                          <td className="px-2 py-3 text-sm font-mono">
                            <Tooltip title={r.usageStartDate} placement="topLeft">
                              <span className="block truncate">{r.usageStartDate}</span>
                            </Tooltip>
                          </td>
                        )}
                        {isColVisible('usageEndDate') && (
                          <td className="px-2 py-3 text-sm font-mono">
                            <Tooltip title={r.usageEndDate} placement="topLeft">
                              <span className="block truncate">{r.usageEndDate}</span>
                            </Tooltip>
                          </td>
                        )}
                        {isColVisible('status') && (
                          <td className="px-2 py-3 truncate">
                            <DemoBadge status={r.status} />
                          </td>
                        )}
                        {isColVisible('accountLocked') && (
                          <td className="px-2 py-3 text-sm">
                            {r.accountLocked ? (
                              <span className="font-medium text-red-700">ロック中</span>
                            ) : (
                              <span className="text-gray-400">－</span>
                            )}
                          </td>
                        )}
                        {isColVisible('lastLoginAt') && (
                          <td className="px-2 py-3 text-sm">
                            <Tooltip title={r.lastLogin} placement="topLeft">
                              <span className="block truncate">{r.lastLogin}</span>
                            </Tooltip>
                          </td>
                        )}
                      <td aria-hidden="true" />
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
          <Pagination
            currentPage={safePage}
            totalPages={totalPages}
            total={total}
            rangeFrom={rangeFrom}
            rangeTo={rangeTo}
            onPageChange={(next) => { setLoading(true); setPage(next); }}
            pageSize={pageSize}
            onPageSizeChange={(next) => { setLoading(true); setPageSize(next); setPage(1); }}
          />
        </div>
      </div>
    </div>
  )
}

export default UserManagement
